<template>
  <v-container fluid>
    <v-flex xs12 fluid>
      <v-card xs2 elevation="0">
        <chat-drawer :drawer="drawer" :single-room="singleRoom" @set-drawer-state="setDrawerState" @set-single-room="setSingleRoom"/>
      </v-card>
      <!-- https://github.com/antoine92190/vue-advanced-chat -->
      <div>
        <!-- overlay to block the access to a room if archived or not joined yet (for public channels) -->
        <message-overlay-channel @open-menu="drawer=!drawer"/>
        <message-overlay-group @open-menu="drawer=!drawer"/>
      <vue-advanced-chat
          v-if="realTimeAPI && (roomId || !singleRoom)"
          class="chat-container"
          :current-user-id="this.currentUser.username"
          :rooms="JSON.stringify(roomsFiltered)"
          :messages="JSON.stringify(messages)"
          :scroll-distance="scrollDistance"
          :room-id="roomId"
          :load-first-room="false"
          :message-actions="JSON.stringify(messageActions)"
          :single-room="singleRoom"
          :theme="this.$vuetify.theme.dark ? 'dark' : 'light'"
          :messages-loaded="messagesLoaded"
          :rooms-loaded="true"
          :is-device="$vuetify.breakpoint.mobile"
          :accepted-files="acceptedFiles"
          :text-messages="JSON.stringify(translations)"
          :rooms-list-opened="false"
          :styles="JSON.stringify(styles)"
          :auto-scroll="JSON.stringify(autoScroll)"
          @open-file="openFile($event.detail[0])"
          @fetch-messages="fetchMessages($event.detail[0])"
          @send-message="sendMessage($event.detail[0])"
          @typing-message="typing($event.detail[0])"
          @add-room="openNewConversationDialog()"
          @send-message-reaction="setMessageReaction($event.detail[0])"
          @edit-message="editMessage($event.detail[0])"
          @delete-message="deleteMessage($event.detail[0])"
          :style="`height: calc(100vh - 55px - 55px);z-index: 1;overflow-y: auto;`"
        >
        <!-- in full mode, override the entiere active room toolbar which includes: drawer toggle, name, typing state, options -->
        <div slot="room-header" v-if="selectedRoom && singleRoom" class="pt-4" style="width: 100%; height: 100%;">
          <v-btn
            @click="drawer=!drawer"
            class="ml-2 mr-2"
            data-testid="chat-open-drawer"
            icon>
            <v-icon v-if="drawer">mdi-menu-open</v-icon>
            <v-icon v-else>mdi-menu</v-icon>
          </v-btn>
          <chat-room-avatar :room="selectedRoom"/>
          <div class="room-title text-truncate">
            <span class="title">{{ selectedRoom.roomName }}</span><br>
            <!-- room type -->
            <span v-if="!roomIsSms" class="caption mr-2">{{ $t(`chat.roomType.${selectedRoom.roomType}`) }}</span>
            <span v-if="roomIsSms" class="caption mr-2">{{ $t('chat.from') }} {{ selectedRoom.fromDid }}</span>
            <v-icon v-if="roomIsGroup" small>mdi-account-group</v-icon>
            <span v-if="roomIsGroup" class="caption mr-2">({{ selectedRoom.users.length }})</span>
            <span v-if="isTyping" class="caption font-italic">{{ $t('chat.typing') }}</span>
          </div>
          <chat-room-actions-menu v-if="selectedRoom" :room="selectedRoom"/>
        </div>
        <!-- in compact mode, override the active room avatar in toolbar -->
        <div slot="room-header-avatar" v-if="selectedRoom && !singleRoom">
          <chat-room-avatar :room="selectedRoom"/>
        </div>
        <!-- in compact mode, override the active room name in toolbar -->
        <div slot="room-header-info" v-if="selectedRoom && !singleRoom">
          <span class="ml-4 text-subtitle-2">{{ selectedRoom.roomName }}</span>
          <!-- room type -->
          <span v-if="selectedRoomIsGroup && !isOnMobile" class="ml-4 mb-1 text-subtitle-2">  {{ $t(`chat.roomType.${selectedRoom.roomType}`) }}</span>
          <v-icon v-if="selectedRoomIsGroup && !isOnMobile" class="ml-4 mb-1">mdi-account-group</v-icon>
          <span v-if="selectedRoomIsGroup && !isOnMobile" class="mb-1 text-subtitle-2">  ({{ selectedRoom.users.length }})</span>
          <span class="ml-4 mb-1 text-subtitle-2 font-italic" v-if="isTyping">{{ $t('chat.typing') }}</span>
        </div>
        <template v-for="room in roomsFiltered">
          <!-- in compact mode, override options in the room list -->
          <div :key="`room-list-options_${room.roomId}`" :slot="`room-list-options_${room.roomId}`" v-if="!singleRoom">
            <chat-room-actions-menu :room="room"/>
          </div>
          <!-- in compact mode, override the avatar in the room list -->
          <div :slot="`room-list-avatar_${room.roomId}`" :key="`room-list-avatar_${room.roomId}`" v-if="!singleRoom">
            <chat-room-avatar :room="room"/>
          </div>
        </template>
        <!-- in compact mode, override options in the room list -->
        <div slot="room-options" v-if="selectedRoom && !singleRoom">
          <chat-room-actions-menu :room="selectedRoom"/>
        </div>
        <!-- for dev purposes. commented for prod, will be reused later, maybe. -->
        <!-- <div slot="rooms-header" v-if="selectedRoom && !singleRoom">
          <v-switch label="Switch mode (dev only)" v-model="singleRoom" class="ml-4"></v-switch>
        </div> -->
      </vue-advanced-chat>
      <v-layout v-else fluid>
        <v-flex xs12>
          <v-img src="/empty_chat.png" class="center" style="transform: scale(0.4);" v-if="singleRoom"></v-img>
          <v-btn v-if="singleRoom && $vuetify.breakpoint.mobile" class="center" @click="drawer=!drawer">
            <v-icon left>
              mdi-menu
            </v-icon>
            {{ $t('chat.openMenu') }}
          </v-btn>
          <v-progress-circular indeterminate class="center" style="height: calc(100vh - 56px - 56px);z-index: 1" :size="60" color="primary" v-if="!singleRoom"></v-progress-circular>
        </v-flex>
      </v-layout>
      </div>
    </v-flex>

    <new-conversation-dialog v-if="currentUser"/>
    <close-direct-chat-dialog/>
    <action-channel-dialog v-if="contextMenuChannel"/>
    <manage-members-dialog v-if="isMembersChannelDialogOpen"/>
    <rename-channel-dialog/>
    <action-group-dialog v-if="contextMenuGroup"/>
    <manage-group-members-dialog v-if="isMembersGroupDialogOpen"/>
    <rename-group-dialog/>
    <contact-form/>
    <contact-details-dialog/>
    <contact-delete-dialog/>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
// import { register } from 'vue-advanced-chat'

import ChatDrawer from './drawer'
import NewConversationDialog from './new_conversation_dialog'
import ManageMembersDialog from './drawer/channels/manage_members_dialog'
import ActionChannelDialog from './drawer/channels/confirm_action_dialog'
import CloseDirectChatDialog from './drawer/direct_chats/close_dialog'
import RenameChannelDialog from './drawer/channels/rename_dialog'
import RenameGroupDialog from './drawer/groups/rename_dialog'
import ManageGroupMembersDialog from './drawer/groups/manage_members_dialog'
import ActionGroupDialog from './drawer/groups/confirm_action_dialog'
import ChatRoomAvatar from '@/components/chat/drawer/compact/avatar'
import ChatRoomActionsMenu from '@/components/chat/drawer/compact/menu_room_actions'
import MixinChatRealtimeApi from '@/mixins/chat/realtime'
import MixinChatRestApi from '@/mixins/chat/rest'
import MixinChatMapping from '@/mixins/chat/mapping'
import MixinChatRoomActions from '@/mixins/chat/room_actions'
import MixinChatMessageActions from '@/mixins/chat/message_actions'
import MixinChatHelper from '@/mixins/chat/helper'
import EmbeddedLocalStorage from '@/api/embedded'
import ContactForm from '@/components/contacts/forms/contact_dialog'
import ContactDetailsDialog from '@/components/contacts/details_dialog'
import ContactDeleteDialog from '@/components/contacts/forms/delete_contact'
import MessageOverlayChannel from '@/components/chat/drawer/channels/message_overlay'
import MessageOverlayGroup from '@/components/chat/drawer/groups/message_overlay'
// work around Jest not compatible (yet) with customElements
// https://developer.mozilla.org/en-US/docs/Web/API/Window/customElements
const { register } = process.env.NODE_ENV !== 'test' ? require('vue-advanced-chat') : {}
if (process.env.NODE_ENV !== 'test') {
  register()
}

export default {
  name: 'chat-panel',
  mixins: [
    MixinChatRealtimeApi,
    MixinChatRestApi,
    MixinChatMapping,
    MixinChatHelper,
    MixinChatRoomActions,
    MixinChatMessageActions
  ],
  data () {
    return {
      acceptedFiles: 'audio/mp3,audio/wav,audio/x-wav,audio/ac3,audio/amr,audio/ogg,image/png,image/jpeg,image/gif,image/bmp,video/mp4,video/webm,video/3gpp,video/H264,video/x-msvideo,text/plain,text/csv,application/pdf,text/richtext,text/calendar,text/directory',
      isTyping: false,
      singleRoom: true,
      drawer: !EmbeddedLocalStorage.get(),
      scrollDistance: 50,
      messages: [],
      reset: true,
      rocketMessages: [], // just for edit msg purposes
      messagesLoaded: false,
      autoScroll: {
        send: {
          new: true, // will scroll down after sending a message
          newAfterScrollUp: true // will not scroll down after sending a message when scrolled up
        },
        receive: {
          new: true, // will not scroll down when receiving a message
          newAfterScrollUp: true // will scroll down when receiving a message when scrolled up
        }
      }
    }
  },
  created () {
    this.auth()
  },
  computed: {
    ...mapGetters({
      currentUser: 'chat/authenticatedRocketChatUser',
      currentPanelUser: 'currentUser/currentUser',
      contextMenuChannel: 'chat/contextMenuChannel',
      isMembersChannelDialogOpen: 'chat/openMembersChannelDialog',
      isMembersGroupDialogOpen: 'chat/openMembersGroupDialog',
      contextMenuGroup: 'chat/contextMenuGroup',
      channels: 'chat/channels',
      directConversations: 'chat/directConversations',
      smsConversations: 'chat/smsConversations',
      groups: 'chat/groups'
    }),

    isOnMobile () {
      return this.$vuetify.breakpoint.mobile
    },

    embedded () {
      return EmbeddedLocalStorage.get()
    },

    roomId () {
      return this.$route?.params?.roomId || null
    },

    roomIsSms () {
      return this.selectedRoom?.roomType === 'd' && this.selectedRoom?.isSms
    },

    roomIsGroup () {
      return this.selectedRoom?.roomType === 'c' || this.selectedRoom?.roomType === 'p'
    },

    selectedRoom () {
      return this.roomsFormatted?.find((room) => { return this.roomId === room.roomId })
    },

    messageActions () {
      return [
        {
          name: 'replyMessage',
          title: this.$t('chat.roomActions.replyMessage')
        },
        {
          name: 'editMessage',
          title: this.$t('chat.roomActions.editMessage'),
          onlyMe: true
        },
        {
          name: 'deleteMessage',
          title: this.$t('chat.roomActions.deleteMessage'),
          onlyMe: true
        }
      ]
    },

    translations () {
      return {
        ROOMS_EMPTY: this.$t('chat.advancedChat.ROOMS_EMPTY'),
        ROOM_EMPTY: this.$t('chat.advancedChat.ROOM_EMPTY'),
        NEW_MESSAGES: this.$t('chat.advancedChat.NEW_MESSAGES'),
        MESSAGE_DELETED: this.$t('chat.advancedChat.MESSAGE_DELETED'),
        MESSAGES_EMPTY: this.$t('chat.advancedChat.MESSAGES_EMPTY'),
        CONVERSATION_STARTED: this.$t('chat.advancedChat.CONVERSATION_STARTED'),
        TYPE_MESSAGE: this.$t('chat.advancedChat.TYPE_MESSAGE'),
        SEARCH: this.$t('chat.advancedChat.SEARCH'),
        IS_ONLINE: this.$t('chat.advancedChat.IS_ONLINE'),
        LAST_SEEN: this.$t('chat.advancedChat.LAST_SEEN'),
        IS_TYPING: this.$t('chat.advancedChat.IS_TYPING'),
        CANCEL_SELECT_MESSAGE: this.$t('chat.advancedChat.CANCEL_SELECT_MESSAGE')
      }
    },

    // https://github.com/antoine92190/vue-advanced-chat/blob/master/src/themes/index.js
    styles () {
      if (this.$vuetify.theme.dark) {
        return {
          message: {
            backgroundMe: '#0277BD'
          }
        }
      } else {
        return {
          message: {
            backgroundMe: '#B3E5FC'
          }
        }
      }
    }
  },
  components: {
    ChatDrawer,
    NewConversationDialog,
    CloseDirectChatDialog,
    ActionChannelDialog,
    RenameChannelDialog,
    ManageMembersDialog,
    RenameGroupDialog,
    ManageGroupMembersDialog,
    ActionGroupDialog,
    ChatRoomAvatar,
    ChatRoomActionsMenu,
    ContactForm,
    ContactDetailsDialog,
    ContactDeleteDialog,
    MessageOverlayChannel,
    MessageOverlayGroup
  },
  methods: {
    ...mapActions({
      auth: 'chat/auth',
      openNewConversationDialog: 'chat/openDialog'
    }),

    setDrawerState (drawer) {
      this.drawer = drawer
    },

    setSingleRoom (singleRoom) {
      this.singleRoom = singleRoom
    }
  }
}
</script>

<style scoped>
.center {
  display: block;
  margin-left: auto;
  margin-right: auto;
  width: 50%;
}

.room-title{
  position: absolute;
  top: 0;
  left: 100px;
  right: 60px;
  line-height: 0rem;
}

.room-title .title{
  line-height: 1.8rem;
}
</style>
